import React, { useEffect, useCallback, useState, useRef } from 'react';
import { navigate } from '@app/router';
import PropTypes from 'prop-types';
import endpoint from '@app/endpoint';
import { setFooterConfig } from '@app/actions/footerActions';
import { resetCustomizeData } from '@app/actions/customizeActions';
import { resetWhiteInkOptionsData } from '@app/actions/whiteInkOptionsActions';
import {
  LOGIN_FORM,
  FORGOT_PASSWORD_FORM,
  SIGNUP_FORM,
  LOGIN_SIGNUP_VIEW,
  CUSTOMIZE_VIEW,
  WHITE_INK_OPTIONS_VIEW,
} from '@app/constants';
import { connect } from 'react-redux';
import useIntlMessage from '@app/hooks/use-intl-message';
import LocalizedText from '@LocalizedText';
import SvgIconUse from '@SvgIconUse';
import { inject } from '@app/providers/QAU.ContextProvider';
import { getAuthorizationStatus } from '@app/actions/authActions';
import { createAccount } from '@app/actions/loginSignupActions';
import { parseSignUpError } from '@app/utils/utils';

import LoginForm from './login-form/LoginForm';
import ForgotPasswordForm from './forgot-password-form/ForgotPasswordForm';
import SignupForm from './signup-form/SignupForm';
import LoginIFrame from './LoginIFrame';

import './LoginSignup.scss';

const LoginSignup = ({
  setFooterConfig,
  flowConfig,
  getAuthorizationStatus,
  createAccount,
  flow,
  resetCustomizeData,
  resetWhiteInkOptionsData,
}) => {
  const frameRef = useRef();
  const [frame, setFrame] = useState(null);
  const [signInError, setSignInError] = useState();
  const [signUpError, setSignUpError] = useState();
  const [signInErrorText] = useIntlMessage('qau-sign-in-invalid');
  const [loginSignupStep, setLoginSignupStep] = useState(LOGIN_FORM);
  const selectLoginSignupStep = step => {
    clearFormErrors();
    setLoginSignupStep(step);
  };

  const navigateBack = useCallback((view) => {
      if (view === CUSTOMIZE_VIEW) {
        flow.editor = null;
        resetCustomizeData();
      }
      if (view === WHITE_INK_OPTIONS_VIEW) {
        resetWhiteInkOptionsData();
      }
      navigate(view, {}, true);
    },
    [flow.editor, resetCustomizeData, resetWhiteInkOptionsData, setFooterConfig],
  );

  const returnToFlow = useCallback(() => {
    const prevView = sessionStorage.getItem('prevView');
    sessionStorage.setItem('prevView', LOGIN_SIGNUP_VIEW);
    navigateBack(prevView);
  }, [navigateBack]);

  const clearFormErrors = () => {
    setSignInError(null);
    setSignUpError(null);
  };

  useEffect(() => {
    setFooterConfig({
      visible: true,
      backHidden: false,
      nextHidden: true,
      onBack: returnToFlow,
    });
  }, [setFooterConfig, returnToFlow]);

  const onSignUp = data => {
    const accountCreated = () => {
      loginCallback();
      onSignIn(data);
    };

    const failed = errors => {
      setSignUpError(parseSignUpError(errors));
    };

    createAccount(flowConfig, data, accountCreated, failed);
  };

  const onSignIn = data => {
    const frameDocument = frame.contentDocument;
    const url = flowConfig.getResourceURL('dpo8-sso-sign-in');
    const clientId = flowConfig.authentication.getParamValue('sso-client-id');

    frame.addEventListener('load', loginCallback);

    frameDocument.getElementById('login').value = data.email;
    frameDocument.getElementById('password').value = data.password;
    frameDocument.getElementById('client_id').value = clientId;
    frameDocument.getElementById('return_url').value = endpoint.url;
    frameDocument.getElementById('login-form').setAttribute('action', url);
    frameDocument.getElementById('login-form').submit();
  };

  const loginSignupStepLayout = {
    [LOGIN_FORM]: <LoginForm {...{ setFooterConfig, selectLoginSignupStep, onSignIn, signInError }} />,
    [FORGOT_PASSWORD_FORM]: <ForgotPasswordForm {...{ selectLoginSignupStep }} />,
    [SIGNUP_FORM]: <SignupForm {...{ selectLoginSignupStep, onSignUp, signUpError }} />,
  };

  const loginCallback = () => {
    getAuthorizationStatus().then(isAuthorized => {
      if (isAuthorized) {
        returnToFlow();
      } else {
        setSignInError(signInErrorText);
        frameRef.current.renderFrame();
      }
    });
  };

  return (
    <div id="login-signup-view-container">
      <div className="left-side">
        <div className="login-signup-dialog__signup details-panel">
          <div className="login-benefits-header">
            <LocalizedText id="qau-account-benefits-title" />
          </div>
          <ul>
            <li className="icon-list">
              <span className="icon">
                <SvgIconUse id="icon-save-project" />
              </span>
              <LocalizedText id="qau-account-benefits-p1" tagName="span" />
            </li>
            <li className="icon-list">
              <span className="icon">
                <SvgIconUse id="icon-easy-apply-design" />
              </span>
              <LocalizedText id="qau-account-benefits-p2" tagName="span" />
            </li>
            <li className="icon-list">
              <span className="icon">
                <SvgIconUse id="icon-join-newsletter" />
              </span>
              <LocalizedText id="qau-account-benefits-p3" tagName="span" />
            </li>
          </ul>
        </div>
      </div>
      <div className="right-side">{loginSignupStepLayout[loginSignupStep]}</div>
      <LoginIFrame ref={frameRef} linkFrame={setFrame} loginCallback={loginCallback} />
    </div>
  );
};

const mapDispatchToProps = {
  setFooterConfig,
  getAuthorizationStatus,
  createAccount,
  resetCustomizeData,
  resetWhiteInkOptionsData,
};

LoginSignup.propTypes = {
  setFooterConfig: PropTypes.func.isRequired,
  selectApplyCodesStep: PropTypes.func.isRequired,
  flowConfig: PropTypes.object.isRequired,
  createAccount: PropTypes.func.isRequired,
  getAuthorizationStatus: PropTypes.func.isRequired,
  resetCustomizeData: PropTypes.func.isRequired,
  resetWhiteInkOptionsData: PropTypes.func.isRequired,
};

export default inject('flow', 'flowConfig')(connect(null, mapDispatchToProps)(LoginSignup));
